import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  Grid,
  GridItem,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox, Input, Select } from '@shared/components';
import { DocumentTypeEnum } from '@shared/types';
import { GenreEnum } from '@shared/types/Genre';
import { links, removeNonNumeric } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useCreateOnboarding } from '../../hooks';
import {
  OnboardingAddResponsibleSchema,
  IOnboardingAddResponsibleForm,
} from '../../schemas';
import { ICreateOnboardingInput } from '../../services';
import { useDataState } from '../../store';

const GENRES = [
  {
    value: GenreEnum.MALE,
    label: 'Masculino',
  },
  {
    value: GenreEnum.FEMALE,
    label: 'Feminino',
  },
];

export const AddResponsibleForm = ({ rootEntity }: { rootEntity: string }) => {
  const { t } = useTranslation(rootEntity);
  const navigate = useNavigate();
  const { onboardingDataCompanyForm } = useDataState();
  const { mutate: createOnboarding, isLoading } = useCreateOnboarding();

  const hookForm = useForm<IOnboardingAddResponsibleForm>({
    resolver: zodResolver(OnboardingAddResponsibleSchema()),
    shouldFocusError: false,
    defaultValues: {
      isTechnicalResponsible: false,
    },
  });

  const handleSubmit = (data: IOnboardingAddResponsibleForm) => {
    const payload: ICreateOnboardingInput = {
      companyName: onboardingDataCompanyForm.name,
      displayName: onboardingDataCompanyForm.fantasyName,
      documentNumber: removeNonNumeric(onboardingDataCompanyForm.document),
      documentType: DocumentTypeEnum.CNPJ,
      sessionAuthId: sessionStorage.getItem('sessionAuthId') || '',
      addressMetadata: {
        postalCode: removeNonNumeric(onboardingDataCompanyForm.postalCode),
        street: onboardingDataCompanyForm.street,
        number: onboardingDataCompanyForm.number,
        complement: onboardingDataCompanyForm.complement,
        district: onboardingDataCompanyForm.district,
        city: onboardingDataCompanyForm.city,
        state: onboardingDataCompanyForm.state,
      },
      phones: [removeNonNumeric(data.phone)],
      persons: !data.isTechnicalResponsible
        ? [
            {
              email: data.email,
              socialName: data.socialName || data.name,
              name: data.name,
              documentNumber: removeNonNumeric(data.documentNumber),
              genre: data.genre,
              birthDate: data.birthDate,
              phones: [removeNonNumeric(data.phone)],
              role: 'OWNER',
            },
            {
              email: data.technicalResponsibleEmail,
              socialName:
                data.technicalResponsibleSocialName ||
                data.technicalResponsibleName,
              name: data.technicalResponsibleName,
              documentNumber: removeNonNumeric(
                data.technicalResponsibleDocumentNumber,
              ),
              genre: data.technicalResponsibleGenre,
              birthDate: data.technicalResponsibleBirthDate,
              phones: [removeNonNumeric(data.technicalResponsiblePhones)],
              role: 'ADMIN',
            },
          ]
        : [
            {
              email: data.email,
              socialName: data.socialName || data.name,
              name: data.name,
              documentNumber: removeNonNumeric(data.documentNumber),
              genre: data.genre,
              birthDate: data.birthDate,
              phones: [removeNonNumeric(data.phone)],
              role: 'OWNER',
            },
          ],
    };

    createOnboarding(payload, {
      onSuccess: () => {
        navigate(links.onboarding.sended);
      },
    });
  };

  return (
    <Flex flexDir="column" gap={4}>
      <FormProvider {...hookForm}>
        <form onSubmit={hookForm.handleSubmit(handleSubmit)}>
          <Flex gap={4} h="100dvh" maxH="calc(100dvh - 272px)" overflowY="auto">
            <Flex flexDir="column" gap={6} w="full">
              <Flex flexDir="column">
                <Text color="brand.800" fontSize="2xl" fontWeight={600}>
                  {t('responsibleData')}
                </Text>

                <Text fontSize="sm" color="neutral.700" fontWeight={400}>
                  {t('responsibleDataDescription')}
                </Text>
              </Flex>

              <Grid templateColumns="repeat(4, 1fr)" gap={4} w="full">
                <GridItem colSpan={4}>
                  <Input
                    label={t('email')}
                    name="email"
                    placeholder={t('emailPlaceholder')}
                  />
                </GridItem>

                <GridItem colSpan={4}>
                  <Alert status="info" justifyContent="start">
                    <AlertIcon />
                    {t('emailAlert')}
                  </Alert>
                </GridItem>
              </Grid>

              <Grid templateColumns="repeat(4, 1fr)" gap={4} w="full">
                <GridItem colSpan={2}>
                  <Input
                    label={t('document')}
                    name="documentNumber"
                    placeholder={t('documentPlaceholder')}
                    type="cpf"
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <Input
                    label={t('socialName')}
                    name="socialName"
                    placeholder={t('socialNamePlaceholder')}
                  />
                </GridItem>

                <GridItem colSpan={4}>
                  <Input
                    label={t('name')}
                    name="name"
                    placeholder={t('name')}
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <Input
                    label={t('birthDate')}
                    name="birthDate"
                    placeholder={t('birthDatePlaceholder')}
                    type="date"
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <Select
                    label={t('genre')}
                    name="genre"
                    placeholder={t('genrePlaceholder')}
                  >
                    {GENRES.map(genre => (
                      <option key={genre.value} value={genre.value}>
                        {genre.label}
                      </option>
                    ))}
                  </Select>
                </GridItem>

                <GridItem colSpan={2}>
                  <Input
                    label={t('phone')}
                    name="phone"
                    placeholder={t('phonePlaceholder')}
                    type="phone"
                  />
                </GridItem>
              </Grid>

              <Flex flexDir="column">
                <Text color="brand.800" fontSize="2xl" fontWeight={600}>
                  {t('technicalResponsibleData')}
                </Text>

                <Text fontSize="sm" color="neutral.700" fontWeight={400}>
                  {t('technicalResponsibleDataDescription')}
                </Text>
              </Flex>

              <Grid templateColumns="repeat(4, 1fr)" gap={4} w="full">
                <GridItem colSpan={4}>
                  <Checkbox
                    p={4}
                    gap={4}
                    name="isTechnicalResponsible"
                    label={t('sameLegalResponsible')}
                  />
                </GridItem>
                {!hookForm.watch('isTechnicalResponsible') && (
                  <>
                    <GridItem colSpan={4}>
                      <Input
                        label={t('email')}
                        name="technicalResponsibleEmail"
                        placeholder={t('emailPlaceholder')}
                      />
                    </GridItem>

                    <GridItem colSpan={4}>
                      <Alert status="info" justifyContent="start">
                        <AlertIcon />
                        {t('emailAlert')}
                      </Alert>
                    </GridItem>

                    <GridItem colSpan={2}>
                      <Input
                        label={t('document')}
                        name="technicalResponsibleDocumentNumber"
                        placeholder={t('documentPlaceholder')}
                        type="cpf"
                      />
                    </GridItem>

                    <GridItem colSpan={2}>
                      <Input
                        label={t('socialName')}
                        name="technicalResponsibleSocialName"
                        placeholder={t('socialNamePlaceholder')}
                      />
                    </GridItem>

                    <GridItem colSpan={4}>
                      <Input
                        label={t('name')}
                        name="technicalResponsibleName"
                        placeholder={t('name')}
                      />
                    </GridItem>

                    <GridItem colSpan={2}>
                      <Input
                        label={t('birthDate')}
                        name="technicalResponsibleBirthDate"
                        placeholder={t('birthDatePlaceholder')}
                        type="date"
                      />
                    </GridItem>

                    <GridItem colSpan={2}>
                      <Select
                        label={t('genre')}
                        name="technicalResponsibleGenre"
                        placeholder={t('genrePlaceholder')}
                      >
                        {GENRES.map(genre => (
                          <option key={genre.value} value={genre.value}>
                            {genre.label}
                          </option>
                        ))}
                      </Select>
                    </GridItem>

                    <GridItem colSpan={2}>
                      <Input
                        label={t('phone')}
                        name="technicalResponsiblePhones"
                        placeholder={t('phonePlaceholder')}
                        type="phone"
                      />
                    </GridItem>
                  </>
                )}
              </Grid>
            </Flex>
          </Flex>

          <Flex justifyContent="space-between">
            <Button
              as={Link}
              to={links.onboarding.companyData}
              colorScheme="brand"
              variant="outline"
              isDisabled={isLoading}
            >
              {t('common:previous')}
            </Button>

            <Button
              type="submit"
              colorScheme="brand"
              w={240}
              isLoading={isLoading}
            >
              {t('sendData')}
            </Button>
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
};
